import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from "@angular/core";

/**
 * Leading `Offset #N` badge for a price row / card: the master catalog's price
 * sequence (`prices[].offset`, 1 = primary price) — the same number the
 * shared `comparePriceOrder` sorts by first, so the badge always explains the
 * order. The "Offset" label keeps it from reading as the card's position;
 * hosts whose column header already names it pass `showLabel = false`. A price
 * without an offset renders nothing, host element included, so it leaves no
 * flex gap behind.
 */
@Component({
  selector: "app-price-offset-badge",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { "[style.display]": "hasOffset() ? null : 'none'" },
  template: `@if (hasOffset()) {
    <span
      class="offset-badge"
      [attr.title]="'Offset ' + offset()"
      data-testid="price-offset-badge"
    >
      @if (showLabel()) {
        <span class="offset-badge__label" data-testid="price-offset-badge-label"
          >Offset</span
        >
      }
      <span data-testid="price-offset-badge-value">#{{ offset() }}</span></span
    >
  }`,
  styles: `
    :host {
      display: inline-flex;
      flex-shrink: 0;
    }
    .offset-badge {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      min-width: 24px;
      height: 18px;
      padding: 0 6px;
      border-radius: 9px;
      background: #eef4ff;
      border: 1px solid #c9dafc;
      color: #1d4ed8;
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }
    .offset-badge__label {
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      opacity: 0.75;
    }
  `,
})
export class PriceOffsetBadge {
  readonly offset = input<number | null | undefined>(null);
  /** Show the "Offset" word before the number. Off where a column header
   *  already names the value. */
  readonly showLabel = input(true);

  protected readonly hasOffset = computed(
    () => typeof this.offset() === "number",
  );
}
